import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { buttonloginstyle } from "../help/Utils";
import { FaArrowRightLong } from "react-icons/fa6";
import { FaCheck } from "react-icons/fa6";
import { selectPackage } from "../API/applicationAPI";
import { createCheckoutSession } from "../API/paymentAPI";
import ButtonLoadingLabel from "./ButtonLoadingLabel";

const SubscriptionPackageCard = ({
  title,
  desc,
  price,
  features = [],
  btnClass,
  packageId,
  applicationId,
  package: packageData,
  currentPackageId,
  disabled = false,
  // Why the button is disabled, when it is not the application's stage — the
  // payment page also disables it until the eligibility agreement is given.
  disabledReason = "Application is not ready for package selection yet",
}) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const isPremiumPackage = (pkg) => {
    if (!pkg) {
      // If package data not provided, check by name (heuristic)
      return title?.toLowerCase().includes("premium") || false;
    }
    // Check if package has tier field
    if ("tier" in pkg) {
      return pkg.tier === "premium";
    }
    // Fallback: check by name
    return pkg.name?.toLowerCase().includes("premium") || false;
  };

  const handlePackageSelection = async () => {
    if (disabled) {
      toast.warning(
        "Your application is not ready for package selection yet. Please ensure your application is in 'qualified' or 'uscis_fee_decision' stage."
      );
      return;
    }

    if (!applicationId) {
      toast.error(
        "No application found. Please complete the eligibility form first."
      );
      return;
    }

    // Prevent multiple simultaneous requests
    if (loading) {
      return;
    }

    try {
      setLoading(true);

      // If the user clicks the already-selected package, skip API call and route accordingly (idempotent)
      if (currentPackageId && Number(currentPackageId) === Number(packageId)) {
        const isPremium = isPremiumPackage(packageData);
        if (isPremium) {
          // Premium packages go to USCIS fee page
          setLoading(false);
          navigate(`/application/${applicationId}/uscis-fee`);
        } else {
          // Basic packages: create checkout URL directly
          try {
            const successUrl = `${window.location.origin}/dashboard?session_id={CHECKOUT_SESSION_ID}&payment_status=success`;
            const cancelUrl = `${window.location.origin}/dashboard?payment_status=cancel`;

            const session = await createCheckoutSession(
              parseInt(applicationId),
              packageId,
              successUrl,
              cancelUrl
            );

            // Redirect directly to Stripe checkout
            window.location.href = session.sessionUrl;
          } catch (checkoutError) {
            console.error("Error creating checkout session:", checkoutError);
            setLoading(false);
            toast.error(
              checkoutError?.response?.data?.message ||
                "Failed to create checkout session. Please try again."
            );
          }
        }
        return;
      }

      // Select the package first
      const updatedApplication = await selectPackage(
        parseInt(applicationId),
        packageId
      );

      // Check if this is a premium package
      const isPremium = isPremiumPackage(packageData);

      if (isPremium) {
        // Premium packages go through USCIS fee decision flow
        setLoading(false);
        navigate(`/application/${applicationId}/uscis-fee`);
      } else {
        // Basic packages: create checkout URL directly from backend and redirect
        try {
          const successUrl = `${window.location.origin}/dashboard?session_id={CHECKOUT_SESSION_ID}&payment_status=success`;
          const cancelUrl = `${window.location.origin}/dashboard?payment_status=cancel`;

          const session = await createCheckoutSession(
            parseInt(applicationId),
            packageId,
            successUrl,
            cancelUrl
          );

          // Redirect directly to Stripe checkout
          window.location.href = session.sessionUrl;
        } catch (checkoutError) {
          console.error("Error creating checkout session:", checkoutError);
          setLoading(false);
          toast.error(
            checkoutError?.response?.data?.message ||
              "Failed to create checkout session. Please try again."
          );
        }
      }
    } catch (error) {
      console.error("Error selecting package:", error);
      const isPremium = isPremiumPackage(packageData);

      // Handle idempotent or conflict errors gracefully by routing forward
      const serverMessage = error?.response?.data?.message || "";
      const httpStatus = error?.response?.status;

      // Handle stage validation error specifically
      if (
        /must be qualified or at uscis fee decision stage/i.test(
          serverMessage
        ) ||
        /must be qualified/i.test(serverMessage)
      ) {
        setLoading(false);
        toast.warning(
          "Your application is not ready for package selection yet. Please complete the eligibility process first or contact support if you believe this is an error."
        );
        // Optionally redirect back to answer review or dashboard
        navigate("/dashboard");
        return;
      }

      if (
        httpStatus === 400 ||
        httpStatus === 409 ||
        httpStatus === 500 ||
        /already has a package/i.test(serverMessage)
      ) {
        // Handle based on package type
        const isPremium = isPremiumPackage(packageData);
        if (isPremium) {
          // Premium packages go to USCIS fee page
          setLoading(false);
          navigate(`/application/${applicationId}/uscis-fee`);
        } else {
          // Basic packages: create checkout URL directly
          try {
            const successUrl = `${window.location.origin}/dashboard?session_id={CHECKOUT_SESSION_ID}&payment_status=success`;
            const cancelUrl = `${window.location.origin}/dashboard?payment_status=cancel`;

            const session = await createCheckoutSession(
              parseInt(applicationId),
              packageId,
              successUrl,
              cancelUrl
            );

            // Redirect directly to Stripe checkout
            window.location.href = session.sessionUrl;
          } catch (checkoutError) {
            console.error("Error creating checkout session:", checkoutError);
            setLoading(false);
            toast.error(
              checkoutError?.response?.data?.message ||
                "Failed to create checkout session. Please try again."
            );
          }
        }
      } else {
        setLoading(false);
        toast.error(serverMessage || "Failed to select package. Please try again.");
      }
    }
  };

  return (
    <div className="px-[35px] py-6 bg-white border border-gray-200 rounded-xl hover:border-[#00B894] transition flex flex-col justify-between">
      <div>
        <h5 className="mb-6 text-2xl leading-10 font-bold text-[#1B365D]">
          {title}
        </h5>
        <p className="text-base text-[#1B365D] mt-2">{desc}</p>
        <h2 className="text-5xl leading-[66px] font-bold text-[#1B365D] my-5">
          {price}
        </h2>

        <ul className="mt-4 space-y-2">
          {features.map((feature, i) => (
            <li
              key={i}
              className="text-[#1B365D] text-base mb-3 flex items-start"
            >
              <FaCheck className="me-3 mt-1" />{" "}
              <span className="inline-block flex-1 text-left">{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      <button
        className={`flex items-center justify-between w-full mt-6 px-5 py-3 rounded-lg text-white font-semibold ${btnClass} ${
          loading || disabled ? "opacity-50 cursor-not-allowed" : ""
        }`}
        style={buttonloginstyle}
        onClick={handlePackageSelection}
        disabled={loading || disabled}
        title={disabled ? disabledReason : ""}
      >
        {loading ? (
          <ButtonLoadingLabel label="Processing" />
        ) : disabled ? (
          "Not Available"
        ) : (
          "Choose Plan"
        )}{" "}
        &nbsp;
        {!loading && !disabled && <FaArrowRightLong className="text-[20px]" />}
      </button>
    </div>
  );
};

export default SubscriptionPackageCard;
